import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay, faXmark } from "@fortawesome/free-solid-svg-icons";
import { Button } from "../../../components/button/Button";
import LoadingOverlay from "../../../components/LoadingOverlay";
import { ModalContext } from "../../../components/Modal";
import SearchInput from "../../../components/SearchInput";
import { showNotification } from "../../../redux/notificationSlice";
import {
  downloadTrainingVideo,
  fetchTrainingVideos,
  TrainingVideoResponse,
} from "../../../redux/adminSlice";
import { AppDispatch } from "../../../redux/store";
import { useDispatch } from "react-redux";
import styles from "./TrainingVideoLibraryModal.module.scss";

const ALL_CATEGORIES = "ALL";

const categoryLabels: Record<string, string> = {
  GETTING_STARTED: "Getting Started",
  PRODUCTS: "Products",
  REPORTS: "Reports",
  ADVANCED: "Advanced",
};

const categoryOrder = Object.keys(categoryLabels);

const getCategoryLabel = (category: string) =>
  categoryLabels[category] ?? category;

type VideoPlayerPopupProps = {
  title: string;
  videoUrl: string;
  onClose: () => void;
};

const VideoPlayerPopup = ({ title, videoUrl, onClose }: VideoPlayerPopupProps) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return createPortal(
    <div className={styles.playerOverlay} onMouseDown={onClose}>
      <div
        className={styles.playerDialog}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className={styles.playerClose}
          onClick={onClose}
          aria-label="Close video player"
          title="Close"
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
        <video
          className={styles.playerVideo}
          src={videoUrl}
          controls
          controlsList="nodownload"
          autoPlay
          playsInline
          aria-label={title}
        />
      </div>
    </div>,
    document.body,
  );
};

type TrainingVideoTileProps = {
  video: TrainingVideoResponse;
  getVideoUrl: (trainingVideoId: number) => Promise<string>;
};

const TrainingVideoTile = ({ video, getVideoUrl }: TrainingVideoTileProps) => {
  const [videoUrl, setVideoUrl] = useState("");
  const [thumbnailUrl, setThumbnailUrl] = useState("");
  const [isLoadingPreview, setIsLoadingPreview] = useState(true);
  const [hasPreviewError, setHasPreviewError] = useState(false);
  const [isPlayerOpen, setIsPlayerOpen] = useState(false);

  useEffect(() => {
    let isActive = true;
    const previewVideo = document.createElement("video");

    const captureThumbnail = () => {
      if (!isActive || !previewVideo.videoWidth || !previewVideo.videoHeight) {
        return;
      }
      const canvas = document.createElement("canvas");
      canvas.width = 640;
      canvas.height = 360;
      const context = canvas.getContext("2d");
      if (context) {
        context.drawImage(previewVideo, 0, 0, canvas.width, canvas.height);
        setThumbnailUrl(canvas.toDataURL("image/jpeg", 0.82));
      }
      setIsLoadingPreview(false);
    };

    const loadVideo = async () => {
      try {
        const objectUrl = await getVideoUrl(video.trainingVideoId);
        if (!isActive) {
          return;
        }
        setVideoUrl(objectUrl);
        previewVideo.preload = "metadata";
        previewVideo.muted = true;
        previewVideo.playsInline = true;
        previewVideo.addEventListener("loadedmetadata", () => {
          const previewTime = Number.isFinite(previewVideo.duration)
            ? Math.min(1, previewVideo.duration / 4)
            : 0;
          if (previewTime > 0) {
            previewVideo.currentTime = previewTime;
          } else {
            captureThumbnail();
          }
        });
        previewVideo.addEventListener("seeked", captureThumbnail, {
          once: true,
        });
        previewVideo.addEventListener("error", () => {
          if (isActive) {
            setHasPreviewError(true);
            setIsLoadingPreview(false);
          }
        });
        previewVideo.src = objectUrl;
      } catch (error) {
        if (isActive) {
          setHasPreviewError(true);
          setIsLoadingPreview(false);
        }
      }
    };

    loadVideo();
    return () => {
      isActive = false;
      previewVideo.removeAttribute("src");
      previewVideo.load();
    };
  }, [getVideoUrl, video.trainingVideoId]);

  return (
    <article className={styles.videoItem}>
      <div className={styles.thumbnail}>
        {thumbnailUrl ? (
          <img src={thumbnailUrl} alt="" />
        ) : (
          <div className={styles.thumbnailPlaceholder}>
            {isLoadingPreview
              ? "Loading ..."
              : hasPreviewError
                ? "Video unavailable"
                : ""}
          </div>
        )}
        <button
          type="button"
          className={styles.playButton}
          onClick={() => setIsPlayerOpen(true)}
          disabled={!videoUrl}
          aria-label={`Play ${video.title}`}
          title={`Play ${video.title}`}
        >
          <FontAwesomeIcon icon={faPlay} />
        </button>
      </div>
      <div className={styles.videoDetails}>
        <h3 title={video.title}>{video.title}</h3>
        <p title={video.description}>{video.description}</p>
      </div>
      {isPlayerOpen && videoUrl && (
        <VideoPlayerPopup
          title={video.title}
          videoUrl={videoUrl}
          onClose={() => setIsPlayerOpen(false)}
        />
      )}
    </article>
  );
};

const TrainingVideoLibraryModal = () => {
  const dispatch = useDispatch<AppDispatch>();
  const modalContext = useContext(ModalContext);
  const close = modalContext?.close ?? (() => {});
  const [videos, setVideos] = useState<TrainingVideoResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [activeCategory, setActiveCategory] = useState(ALL_CATEGORIES);
  const [searchValue, setSearchValue] = useState("");
  const videoListRequestRef = useRef<Promise<TrainingVideoResponse[]> | null>(
    null,
  );
  const videoUrlCacheRef = useRef(new Map<number, Promise<string>>());
  const objectUrlsRef = useRef(new Set<string>());
  const disposalTimerRef = useRef<number | null>(null);
  const isDisposedRef = useRef(false);

  const getVideoUrl = useCallback(
    (trainingVideoId: number) => {
      const cachedRequest = videoUrlCacheRef.current.get(trainingVideoId);
      if (cachedRequest) {
        return cachedRequest;
      }

      const request = dispatch(downloadTrainingVideo(trainingVideoId))
        .unwrap()
        .then((videoBlob) => {
          const objectUrl = URL.createObjectURL(videoBlob);
          if (isDisposedRef.current) {
            URL.revokeObjectURL(objectUrl);
            throw new Error("Training video library was closed");
          }
          objectUrlsRef.current.add(objectUrl);
          return objectUrl;
        })
        .catch((error) => {
          videoUrlCacheRef.current.delete(trainingVideoId);
          throw error;
        });
      videoUrlCacheRef.current.set(trainingVideoId, request);
      return request;
    },
    [dispatch],
  );

  useEffect(() => {
    isDisposedRef.current = false;
    if (disposalTimerRef.current !== null) {
      window.clearTimeout(disposalTimerRef.current);
      disposalTimerRef.current = null;
    }

    return () => {
      disposalTimerRef.current = window.setTimeout(() => {
        isDisposedRef.current = true;
        objectUrlsRef.current.forEach((objectUrl) =>
          URL.revokeObjectURL(objectUrl),
        );
        objectUrlsRef.current.clear();
        videoUrlCacheRef.current.clear();
      }, 0);
    };
  }, []);

  const loadVideos = async () => {
    setIsLoading(true);
    setHasError(false);
    try {
      if (!videoListRequestRef.current) {
        videoListRequestRef.current = dispatch(fetchTrainingVideos()).unwrap();
      }
      const response = await videoListRequestRef.current;
      setVideos(response);
    } catch (error) {
      videoListRequestRef.current = null;
      setHasError(true);
      dispatch(
        showNotification({
          message: "Error loading training videos. Please contact admin.",
          duration: 3000,
          isError: true,
        }),
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadVideos();
  }, []);

  const availableCategories = Array.from(
    new Set(videos.map((video) => video.category).filter(Boolean)),
  ).sort((firstCategory, secondCategory) => {
    const firstIndex = categoryOrder.indexOf(firstCategory);
    const secondIndex = categoryOrder.indexOf(secondCategory);
    if (firstIndex === -1 && secondIndex === -1) {
      return firstCategory.localeCompare(secondCategory);
    }
    if (firstIndex === -1) return 1;
    if (secondIndex === -1) return -1;
    return firstIndex - secondIndex;
  });

  const normalizedSearch = searchValue.trim().toLowerCase();
  const filteredVideos = videos.filter((video) => {
    const matchesCategory =
      activeCategory === ALL_CATEGORIES || video.category === activeCategory;
    if (!normalizedSearch) {
      return matchesCategory;
    }
    const matchesSearch =
      (video.title ?? "").toLowerCase().includes(normalizedSearch) ||
      (video.description ?? "").toLowerCase().includes(normalizedSearch);
    return matchesCategory && matchesSearch;
  });

  const selectCategory = (category: string) => {
    setActiveCategory(category);
  };

  const updateSearch = (value: string) => {
    setSearchValue(value);
  };

  return (
    <section
      className={styles.library}
      role="dialog"
      aria-modal="true"
      aria-labelledby="training-video-library-title"
    >
      <header className={styles.header}>
        <div>
          <h2 id="training-video-library-title">Training Videos</h2>
          <p>Browse product guidance and learning resources.</p>
        </div>
        <div className={styles.headerActions}>
          <div className={styles.search}>
            <SearchInput
              callback={updateSearch}
              searchManualCallback={updateSearch}
              debounce={250}
              placeholder="Search videos"
            />
          </div>
        </div>
      </header>

      <div className={styles.tabs} role="tablist" aria-label="Video categories">
        {[ALL_CATEGORIES, ...availableCategories].map((category) => (
          <div
            key={category}
            role="tab"
            tabIndex={0}
            aria-selected={activeCategory === category}
            className={`${styles.tabItem} ${
              activeCategory === category ? styles.activeTab : ""
            }`}
            onClick={() => selectCategory(category)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                selectCategory(category);
              }
            }}
          >
            <span className={styles.tabLabel}>
              {category === ALL_CATEGORIES ? "All" : getCategoryLabel(category)}
            </span>
          </div>
        ))}
      </div>

      <div className={styles.content}>
        {isLoading ? (
          <div className={styles.state}>
            <LoadingOverlay show  />
          </div>
        ) : hasError ? (
          <div className={styles.state}>
            <h3>Videos could not be loaded</h3>
            <p>Try loading the training library again.</p>
            <Button
              onClick={loadVideos}
              className="btn md"
              color="primary"
              variant="outline"
            >
              Retry
            </Button>
          </div>
        ) : filteredVideos.length === 0 ? (
          <div className={styles.state}>
            <h3>No videos found</h3>
            <p>Try another category or search term.</p>
          </div>
        ) : (
          <div className={styles.videoGrid}>
            {filteredVideos.map((video) => (
              <TrainingVideoTile
                key={video.trainingVideoId}
                video={video}
                getVideoUrl={getVideoUrl}
              />
            ))}
          </div>
        )}
      </div>

      <footer className={styles.footer}>
        <span className={styles.resultCount}>
          {!isLoading && !hasError && filteredVideos.length > 0
            ? `${filteredVideos.length} video${
                filteredVideos.length === 1 ? "" : "s"
              }`
            : ""}
        </span>
        <Button
          onClick={close}
          className={`btn md ${styles.footerClose}`}
          color="primary"
          variant="outline"
        >
          Close
        </Button>
      </footer>
    </section>
  );
};

export default TrainingVideoLibraryModal;
